/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * The Feature Override screen's focus layout, as pure functions.
 *
 * Each feature now carries two independent controls, so a single flat control
 * index is what UP/DOWN walks — the same order the screen renders:
 *
 *   index          control
 *   ─────────────  ────────────────────────────────────────────
 *   0              rows[0]  Stage
 *   1              rows[0]  Activation Override
 *   2              rows[1]  Stage
 *   3              rows[1]  Activation Override
 *   …
 *   2n             Reset All Stage Overrides
 *   2n + 1         Reset All Feature Overrides
 *   2n + 2         Video Debug Overlay switch
 *
 * The slice, the D-pad interceptor and the presentational screen all derive
 * focus from these helpers, so the three can never disagree about the layout.
 */

import { FeatureOverrideControlKind } from "./featureOverride.types";
import type { TFeatureOverrideControl } from "./featureOverride.types";

/** Controls per feature row: Stage, then Activation Override. */
export const CONTROLS_PER_ROW: number = 2;

/** Trailing reset buttons: Reset Stage Overrides, Reset Feature Overrides. */
export const RESET_CONTROL_COUNT: number = 2;

/**
 * Trailing controls that are not resets: currently just the Video Debug
 * Overlay switch. Counted separately so the reset indices, which callers
 * and tests already depend on, keep their positions.
 */
export const EXTRA_CONTROL_COUNT: number = 1;

/** Total focusable controls for `rowCount` features. */
export function getControlCount(rowCount: number): number {
  return (
    rowCount * CONTROLS_PER_ROW + RESET_CONTROL_COUNT + EXTRA_CONTROL_COUNT
  );
}

/** Index of the "Reset All Stage Overrides" button. */
export function getResetStageIndex(rowCount: number): number {
  return rowCount * CONTROLS_PER_ROW;
}

/** Index of the "Reset All Feature Overrides" button. */
export function getResetActivationIndex(rowCount: number): number {
  return rowCount * CONTROLS_PER_ROW + 1;
}

/** Index of the "Video Debug Overlay" switch. */
export function getVideoDebugOverlayIndex(rowCount: number): number {
  return rowCount * CONTROLS_PER_ROW + RESET_CONTROL_COUNT;
}

/** Focus index of one row's Stage control. */
export function getStageControlIndex(rowIndex: number): number {
  return rowIndex * CONTROLS_PER_ROW;
}

/** Focus index of one row's Activation Override control. */
export function getActivationControlIndex(rowIndex: number): number {
  return rowIndex * CONTROLS_PER_ROW + 1;
}

/**
 * What a focus index addresses, or `null` when it falls outside the layout.
 */
export function resolveControl(
  index: number,
  rowCount: number
): TFeatureOverrideControl | null {
  if (index < 0 || index >= getControlCount(rowCount)) {
    return null;
  }
  if (index === getResetStageIndex(rowCount)) {
    return { kind: FeatureOverrideControlKind.RESET_STAGE, rowIndex: -1 };
  }
  if (index === getResetActivationIndex(rowCount)) {
    return { kind: FeatureOverrideControlKind.RESET_ACTIVATION, rowIndex: -1 };
  }
  if (index === getVideoDebugOverlayIndex(rowCount)) {
    return {
      kind: FeatureOverrideControlKind.VIDEO_DEBUG_OVERLAY,
      rowIndex: -1
    };
  }
  const rowIndex: number = Math.floor(index / CONTROLS_PER_ROW);
  const isStage: boolean = index % CONTROLS_PER_ROW === 0;
  return {
    kind: isStage
      ? FeatureOverrideControlKind.STAGE
      : FeatureOverrideControlKind.ACTIVATION,
    rowIndex
  };
}
